<template>
  <div
    ref="rootEl"
    data-controller="multiple-search"
    :data-multiple-search-options-value="JSON.stringify(props.options)"
    :data-multiple-search-selected-value="JSON.stringify(props.selectedItems)"
    :data-multiple-search-use-id-value="props.useId"
    :data-multiple-search-prefix-value="props.prefix"
    :data-multiple-search-close-on-select-value="props.closeOnSelect"
    :data-multiple-search-autosubmit-value="props.autosubmit"
    :data-multiple-search-placeholder-value="props.placeholder"
  >
    <div class="relative">
      <!-- Field wrapper containing pills and search input -->
      <!-- v-once prevents Vue from re-rendering when selectedItems changes; Stimulus manages pills after initial render -->
      <div
        v-once
        :id="`${props.prefix}_pills`"
        data-multiple-search-target="pills"
        class="input-combobox-field"
      >
        <!-- Render initial pills based on selectedItems prop -->
        <div
          v-for="item in initialPills"
          :key="item.id"
          data-multiple-search-target="pill"
          data-search-pill
          :data-id="item.id"
          class="input-combobox-pill"
          :id="`${props.prefix}_pill_${item.id}`"
        >
          <span data-text class="truncate">{{ item.label }}</span>
          <button
            type="button"
            data-action="multiple-search#remove"
            class="input-combobox-pill-remove"
            :data-id="item.id"
          >
            <span class="sr-only">Remove {{ item.label }}</span>
            <svg class="size-4 stroke-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Search input wrapper -->
        <div class="input-combobox-search-wrapper">
          <input
            type="text"
            autocomplete="off"
            :placeholder="initialPills.length > 0 ? '' : props.placeholder"
            data-multiple-search-target="search"
            data-action="keyup->multiple-search#search focus->multiple-search#open"
            role="combobox"
            :aria-controls="`${props.prefix}_options`"
            aria-expanded="false"
            class="input-combobox-search"
          />
        </div>
      </div>

      <!-- Options dropdown -->
      <ul
        data-multiple-search-target="list"
        :id="`${props.prefix}_options`"
        role="listbox"
        class="input-combobox-options hidden"
      ></ul>

    </div>

    <!-- Render initial hidden inputs (v-once prevents re-rendering; Stimulus manages inputs after initial render) -->
    <div v-once style="display: contents;">
      <input
        v-for="item in initialPills"
        :key="`input-${item.id}`"
        type="hidden"
        multiple
        data-search-input
        :name="`${props.prefix}_ids[]`"
        :value="item.id"
        :id="`${props.prefix}_input_${item.id}`"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'

const props = defineProps({
  options: {
    type: Array,
    required: true,
    default: () => [] // Expected format: [[label1, id1], [label2, id2], ...]
  },
  selectedItems: {
    type: Array,
    required: true,
    default: () => [] // Expected format: [[label1, id1], [label2, id2], ...]
  },
  useId: {
    type: Boolean,
    default: true
  },
  prefix: {
    type: String,
    required: true
  },
  closeOnSelect: {
    type: Boolean,
    default: false
  },
  autosubmit: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: 'Search'
  },
  allowCreate: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:selectedItems'])
const rootEl = ref(null)

const initialPills = computed(() => {
  return props.selectedItems.map(pair => ({ label: pair[0], id: pair[1] }))
})

// Create native HTML templates programmatically to avoid Vue's template processing
const createTemplates = () => {
  if (!rootEl.value) return

  const relativeContainer = rootEl.value.querySelector('.relative')
  if (!relativeContainer) return

  // Option template
  const optionTemplate = document.createElement('template')
  optionTemplate.setAttribute('data-multiple-search-target', 'optionTemplate')
  optionTemplate.innerHTML = `
    <li class="input-combobox-option" data-action="click->multiple-search#select" role="option" tabindex="-1">
      <span data-input-value class="block truncate"></span>
    </li>
  `
  relativeContainer.appendChild(optionTemplate)

  // Input template
  const inputTemplate = document.createElement('template')
  inputTemplate.setAttribute('data-multiple-search-target', 'inputTemplate')
  inputTemplate.innerHTML = `<input type="hidden" multiple data-search-input="true" />`
  relativeContainer.appendChild(inputTemplate)

  // Pill template
  const pillTemplate = document.createElement('template')
  pillTemplate.setAttribute('data-multiple-search-target', 'pillTemplate')
  pillTemplate.innerHTML = `
    <div data-multiple-search-target="pill" data-search-pill="true" class="input-combobox-pill">
      <span data-text class="truncate">template</span>
      <button type="button" data-action="multiple-search#remove" class="input-combobox-pill-remove">
        <span class="sr-only">Remove</span>
        <svg class="size-4 stroke-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  `
  relativeContainer.appendChild(pillTemplate)

  // Create option template (for allowCreate)
  if (props.allowCreate) {
    const createOptionTemplate = document.createElement('template')
    createOptionTemplate.setAttribute('data-multiple-search-target', 'createOption')
    createOptionTemplate.innerHTML = `
      <li class="input-combobox-option" data-action="click->multiple-search#createOption" role="option" tabindex="-1">
        <div class="flex items-center gap-3">
          <svg class="size-4 stroke-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          <span data-input-value></span>
        </div>
      </li>
    `
    relativeContainer.appendChild(createOptionTemplate)
  }
}

const syncFromStimulus = () => {
  const pills = document.querySelectorAll(`[data-multiple-search-prefix-value="${props.prefix}"] [data-search-pill]`)
  const newSelectedIds = Array.from(pills).map(p => p.dataset.id)

  const newSelectedItems = newSelectedIds.map(id => {
    const optionPair = props.options.find(pair => String(pair[1]) === String(id))
    const selectedPair = props.selectedItems.find(pair => String(pair[1]) === String(id))
    const label = optionPair ? optionPair[0] : (selectedPair ? selectedPair[0] : 'Unknown')
    return [label, id]
  })

  emit('update:selectedItems', newSelectedItems)
}

onMounted(() => {
  createTemplates()
  window.addEventListener('pillinput:selected', syncFromStimulus)
  window.addEventListener('pillinput:removed', syncFromStimulus)
})

onBeforeUnmount(() => {
  window.removeEventListener('pillinput:selected', syncFromStimulus)
  window.removeEventListener('pillinput:removed', syncFromStimulus)
})
</script>
